
import { EtsyOrder } from "@/types/etsy";

interface ColumnConfig {
  key: keyof EtsyOrder | 'formattedAddress' | 'itemTitle' | 'itemQuantity';
  label: string;
  enabled: boolean;
}

export const exportOrdersToCSV = (orders: EtsyOrder[], selectedColumns?: ColumnConfig[]) => {
  // Default columns if none provided (for backward compatibility)
  const defaultColumns: ColumnConfig[] = [
    { key: 'receiptId', label: 'Receipt ID', enabled: true },
    { key: 'customerName', label: 'Customer Name', enabled: true },
    { key: 'customerEmail', label: 'Customer Email', enabled: true },
    { key: 'productName', label: 'Product Name', enabled: true },
    { key: 'itemTitle', label: 'Item Title', enabled: false },
    { key: 'itemQuantity', label: 'Item Quantity', enabled: false },
    { key: 'formattedAddress', label: 'Full Address', enabled: true },
    { key: 'city', label: 'City', enabled: false },
    { key: 'zip', label: 'ZIP Code', enabled: false },
    { key: 'country', label: 'Country', enabled: true },
    { key: 'date', label: 'Order Date', enabled: true },
    { key: 'status', label: 'Order Status', enabled: true },
    { key: 'subtotalAmount', label: 'Net Revenue', enabled: true },
    { key: 'totalAmount', label: 'Total Amount', enabled: true },
    { key: 'currencyCode', label: 'Currency', enabled: true },
    { key: 'trackingId', label: 'Tracking ID', enabled: false }
  ];

  const columnsToUse = selectedColumns || defaultColumns;
  const enabledColumns = columnsToUse.filter(col => col.enabled);
  
  const headers = enabledColumns.map(col => col.label);

  // Helper function to escape CSV values
  const escapeCsvValue = (value: any): string => {
    if (value === null || value === undefined) {
      return '';
    }
    
    const stringValue = String(value);
    
    // If the value contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n') || stringValue.includes('\r')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    
    return stringValue;
  };

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid
      }
      return date.toLocaleDateString(); // Format as MM/DD/YYYY or based on locale
    } catch {
      return dateString;
    }
  };

  // Helper function to format address
  const formatAddress = (order: EtsyOrder): string => {
    const parts = [];
    if (order.address && order.address.trim() && order.address !== 'N/A') {
      parts.push(order.address.trim());
    }
    if (order.city && order.city.trim()) {
      parts.push(order.city.trim());
    }
    if (order.zip && order.zip.trim()) {
      parts.push(order.zip.trim());
    }
    return parts.length > 0 ? parts.join(', ') : 'Address not provided';
  };

  // Helper function to get value for a specific column
  const getColumnValue = (order: EtsyOrder, columnKey: string, currentItem?: any): any => {
    switch (columnKey) {
      case 'formattedAddress':
        return formatAddress(order);
      case 'date':
        return formatDate(order.date);
      case 'subtotalAmount':
        return order.subtotalAmount || 0;
      case 'totalAmount':
        return order.totalAmount || 0;
      case 'customerEmail':
        return order.customerEmail || '';
      case 'address':
        return order.address || '';
      case 'city':
        return order.city || '';
      case 'zip':
        return order.zip || '';
      case 'country':
        return order.country || '';
      case 'currencyCode':
        return order.currencyCode || 'USD';
      case 'trackingId':
        return order.trackingId || 'N/A';
      case 'itemTitle':
        return currentItem ? currentItem.title : order.productName;
      case 'itemQuantity':
        return currentItem ? currentItem.quantity || 1 : 1;
      default:
        return order[columnKey as keyof EtsyOrder] || '';
    }
  };

  // Create rows - duplicate for each item if order has multiple items
  const csvRows: string[] = [];
  
  orders.forEach(order => {
    if (order.items && order.items.length > 1) {
      // Create a row for each item
      order.items.forEach(item => {
        const row = enabledColumns.map(col => 
          escapeCsvValue(getColumnValue(order, String(col.key), item))
        ).join(',');
        csvRows.push(row);
      });
    } else {
      // Single item or no items array - create one row
      const row = enabledColumns.map(col => 
        escapeCsvValue(getColumnValue(order, String(col.key)))
      ).join(',');
      csvRows.push(row);
    }
  });

  const csvContent = [
    headers.join(','),
    ...csvRows
  ].join('\n');

  // Create and download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `orders-export-${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
